import React, { useEffect, useState } from "react";
import { useController } from "react-hook-form";
import { useTranslate, useDataProvider, useRecordContext } from "react-admin";
import { Card, CardContent, Typography, CircularProgress, Box, Avatar, Divider } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { styled } from "@mui/material/styles";



// Styled component for RTL timeline
const TimelineWrapper = styled(Box)({
    display: "flex",
    flexDirection: "column",
    position: "relative",
    paddingRight: "24px", // Adjusted for RTL
    borderRight: "2px solid #d1d5db", // Light gray border
    textAlign: "right", // Align text properly
});

const TimelineItem = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    marginBottom: "16px",
    position: "relative",
    flexDirection: "row-reverse", // For correct RTL layout
    backgroundColor: theme.palette.mode === "dark" ? "#f9fafb" : "#ffffff",
    padding: "12px",
    borderRadius: "8px",
    boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)", // Light shadow effect
}));

// Styled Avatar for timeline icons
const TimelineIcon = styled(Avatar)(({ theme }) => ({
    width: 28,
    height: 28,
    backgroundColor: "#ffffff", // White background for icons
    border: `2px solid #d1d5db`, // Light gray border
    position: "absolute",
    right: "-20px", // Adjusted for RTL positioning
}));

/**
 * Generic TimelineInput Component for displaying logs (Light Theme, RTL Support)
 *
 * @param {Object} props
 * @param {string} props.field_meta - JSON metadata defining field structure
 * @param {string} props.source_name - Field name used in the form
 */
export const TimelineInput = ({ field_meta, source_name, ...props }) => {
    const translate = useTranslate();
    const meta = JSON.parse(field_meta);
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);

    // Extracting metadata properties
    const resourceName = meta.resource || "accident_approval_log";
    const foreignKey = meta.foreign_key || "accident_id";
    const fields = meta.fields || ["action_by", "action", "action_date"];
    const label = meta.label ? meta.label : translate(source_name);

    useEffect(() => {
        if (!record) return;
        setLoading(true);
        dataProvider
            .getList(resourceName, {
                filter: { [foreignKey]: record.id },
                sort: { field: "action_date", order: "DESC" },
                pagination: { page: 1, perPage: meta.perPage || 10 },
            })
            .then(({ data }) => setLogs(data))
            .finally(() => setLoading(false));
    }, [record, dataProvider, resourceName, foreignKey]);

    const {
        fieldState: { error },
    } = useController({
        name: source_name,
        rules: {},
        defaultValue: "",
        ...props,
    });

    if (loading) return <CircularProgress size={24} />;

    return (
        <Box sx={{ mt: 1, p: 0, borderRadius: 2, backgroundColor: "#ffffff", width: "100%", direction: "rtl" }}>
            {/* Header with dark blue background */}
            <Box sx={{ backgroundColor: "#0a2540", color: "#ffffff", padding: "12px", borderRadius: "8px 8px 0 0" }}>
                <Typography variant="h6">{label}</Typography>
            </Box>

            <Card sx={{ maxHeight: meta.height || 200, overflowY: "auto", p: 2, backgroundColor: "#f9fafb" }}>
                {logs.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                        {translate("ra.message.no_records")}
                    </Typography>
                ) : (
                    <TimelineWrapper>
                        {logs.map((log, index) => (
                            <TimelineItem key={log.id} sx={{ backgroundColor: index % 2 === 0 ? "#f3f4f6" : "#ffffff" }}>
                                {/* Icon Logic */}
                                <TimelineIcon>
                                    {log?.action === "رفض" ? (
                                        <CancelIcon sx={{ color: "#e63946" }} />
                                    ) : log?.action?.includes?.("Assign") ? (
                                        <AccessTimeIcon sx={{ color: "#f4a261" }} />
                                    ) : index === logs.length - 1 ? (
                                        <ArrowUpwardIcon sx={{ color: "#2a9d8f" }} />
                                    ) : (
                                        <CheckCircleIcon sx={{ color: "#2a9d8f" }} />
                                    )}
                                </TimelineIcon>

                                {/* Action Details */}
                                <Box sx={{ mr: 3, width: "100%" }}>
                                    <Typography variant="body2" sx={{ fontWeight: "bold", color: "#1e293b" }}>
                                        {log.action}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "#4b5563" }}>
                                        {log.action_by_role}: <strong>{log.action_by}</strong>
                                    </Typography>
                                    {log.rejection_reason && (
                                        <Typography variant="body2" sx={{ color: "#e63946" }}>
                                            سبب الرفض: {log.rejection_reason}
                                            {log.rejection_details ? ` - ${log.rejection_details}` : ""}

                                        </Typography>
                                    )}
                                    <Typography variant="caption" sx={{ color: "#6b7280", position: "absolute", left: 0 }}>
                                        {new Date(log.action_date).toLocaleString()}
                                    </Typography>
                                </Box>
                            </TimelineItem>
                        ))}
                    </TimelineWrapper>
                )}
            </Card>

            {error && (
                <Typography variant="caption" color="error">
                    {error.message}
                </Typography>
            )}
        </Box>
    );
};
/**
 * TimelineShow Component for displaying logs in Show View (Light Theme, RTL Support)
 *
 * @param {Object} props
 * @param {string} props.field_meta - JSON metadata defining field structure
 * @param {string} props.source_name - Field name used in the record
 */
export const TimelineShow = ({ field_meta, source_name }) => {
    const translate = useTranslate();
    const meta = JSON.parse(field_meta);
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);

    // Extract metadata properties
    const resourceName = meta.resource || "accident_approval_log";
    const foreignKey = meta.foreign_key || "accident_id";
    const label = meta.label ? meta.label : translate(source_name);

    useEffect(() => {
        if (!record) return;
        setLoading(true);
        dataProvider
            .getList(resourceName, {
                filter: { [foreignKey]: record.id },
                sort: { field: "action_date", order: "DESC" },
                pagination: { page: 1, perPage: meta.perPage || 10 },
            })
            .then(({ data }) => setLogs(data))
            .finally(() => setLoading(false));
    }, [record, dataProvider, resourceName, foreignKey]);

    if (loading) return <CircularProgress size={24} />;

    return (
        <Box sx={{ mt: 1, p: 0, borderRadius: 2, backgroundColor: "#ffffff", width: "100%", direction: "rtl" }}>
            <Box sx={{ backgroundColor: "#0a2540", color: "#ffffff", padding: "12px", borderRadius: "8px 8px 0 0" }}>
                <Typography variant="h6">{label}</Typography>
            </Box>

            <Card sx={{ maxHeight: meta.height || 200, overflowY: "auto", p: 2, backgroundColor: "#f9fafb" }}>
                {logs.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                        {translate("ra.message.no_records")}
                    </Typography>
                ) : (
                    <TimelineWrapper>
                        {logs.map((log, index) => (
                            <TimelineItem key={log.id} sx={{ backgroundColor: index % 2 === 0 ? "#f3f4f6" : "#ffffff" }}>
                                <TimelineIcon>
                                    {log?.action === "رفض" ? (
                                        <CancelIcon sx={{ color: "#e63946" }} />
                                    ) : log?.action?.includes?.("Assign") ? (
                                        <AccessTimeIcon sx={{ color: "#f4a261" }} />
                                    ) : index === logs.length - 1 ? (
                                        <ArrowUpwardIcon sx={{ color: "#2a9d8f" }} />
                                    ) : (
                                        <CheckCircleIcon sx={{ color: "#2a9d8f" }} />
                                    )}
                                </TimelineIcon>

                                <Box sx={{ mr: 3, width: "100%" }}>
                                    <Typography variant="body2" sx={{ fontWeight: "bold", color: "#1e293b" }}>
                                        {log.action}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "#4b5563" }}>
                                        {log.action_by_role}: <strong>{log.action_by}</strong>
                                    </Typography>
                                    {log.rejection_reason && (
                                        <Typography variant="body2" sx={{ color: "#e63946" }}>
                                            سبب الرفض: {log.rejection_reason}
                                            {log.rejection_details ? ` - ${log.rejection_details}` : ""}
                                        </Typography>
                                    )}
                                    <Typography variant="caption" sx={{ color: "#6b7280", position: "absolute", left: 0 }}>
                                        {new Date(log.action_date).toLocaleString()}
                                    </Typography>
                                </Box>
                            </TimelineItem>
                        ))}
                    </TimelineWrapper>
                )}
            </Card>
        </Box>
    );
};